"use client";

import * as React from "react";
import { Layers, Maximize2, ShieldCheck, Cpu, ArrowRight, SlidersHorizontal, CircleCheck, FileSpreadsheet, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import InlineCta from "@/components/common/InlineCta";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
interface ComponentTypeItem {
  title: L6;
  category: L6;
  description: L6;
  specs: L6;
  link: string;
  linkLabel: L6;
}
const componentTypes: ComponentTypeItem[] = [{
  title: {
    en: "Filter components",
    zh: "过滤组件与加工件",
    es: "Componentes de filtración",
    de: "Filterkomponenten & Fertigungsteile",
    fr: "Composants de filtration & pièces façonnées",
    pt: "Componentes de filtração e peças usinadas"
  },
  category: {
    en: "Fabricated parts",
    zh: "深加工定制件",
    es: "Piezas procesadas a medida",
    de: "Gefertigte Sonderteile",
    fr: "Pièces usinées sur mesure",
    pt: "Peças fabricadas sob medida"
  },
  description: {
    en: "Custom stamped discs, cylindrical strainers, pleated packs, and framed baskets built for continuous industrial assembly lines.",
    zh: "定制冲压滤圆片、圆筒滤网、折叠滤芯及包边网篮，适用于工业连续装配线。",
    es: "Discos estampados a medida, filtros cilíndricos, paquetes plisados y cestas enmarcadas para líneas continuas de montaje industrial.",
    de: "Gestanzte Ronden, Zylindersiebe, Plisseepakete und gefasste Körbe für kontinuierliche industrielle Fertigungslinien.",
    fr: "Disques emboutis sur mesure, crépines cylindriques, filtres plissés et paniers sertis pour lignes d'assemblage industriel continu.",
    pt: "Discos estampados personalizados, filtros cilíndricos, pacotes plissados e cestos com moldura para linhas industriais contínuas."
  },
  specs: {
    en: "Mesh counts 1 to 635 | 304 / 316L",
    zh: "目数范围 1 至 635 目 | 304 / 316L",
    es: "Mallas de 1 a 635 | 304 / 316L",
    de: "Maschen 1 bis 635 | 304 / 316L",
    fr: "Mailles 1 à 635 | 304 / 316L",
    pt: "Malhas de 1 a 635 | 304 / 316L"
  },
  link: "/FilterComponentsPage",
  linkLabel: {
    en: "Explore filter components",
    zh: "浏览过滤组件",
    es: "Explorar componentes de filtración",
    de: "Filterkomponenten entdecken",
    fr: "Découvrir les composants de filtration",
    pt: "Explorar componentes de filtração"
  }
}, {
  title: {
    en: "Filter cartridges",
    zh: "工业滤芯与滤筒",
    es: "Cartuchos filtrantes",
    de: "Filterkerzen & Zylinder",
    fr: "Cartouches filtrantes",
    pt: "Cartuchos filtrantes"
  },
  category: {
    en: "High-pressure elements",
    zh: "高压耐压元件",
    es: "Elementos de alta presión",
    de: "Hochdruck-Elemente",
    fr: "Éléments haute pression",
    pt: "Elementos de alta pressão"
  },
  description: {
    en: "Rigid all-welded stainless steel cartridges engineered for extreme differential pressure, thermal cycling, and chemical resistance.",
    zh: "全焊接高刚性不锈钢滤芯，专为极端压差、高低温热循环及强化学耐受性设计。",
    es: "Cartuchos rígidos totalmente soldados de acero inoxidable diseñados para presión diferencial extrema, ciclos térmicos y resistencia química.",
    de: "Starre, vollverschweißte Edelstahlfilterkerzen für extreme Differenzdrücke, thermische Wechselfestigkeit und chemische Beständigkeit.",
    fr: "Cartouches rigides entièrement soudées en acier inoxydable pour pressions différentielles extrêmes, cyclages thermiques et résistance chimique.",
    pt: "Cartuchos rígidos totalmente soldados em aço inoxidável para extrema pressão diferencial, ciclagem térmica e resistência química."
  },
  specs: {
    en: "Micron ratings 1 to 200 µm | Cleanable design",
    zh: "过滤精度 1 至 200 µm | 可反洗清洗结构",
    es: "Micraje de 1 a 200 µm | Diseño lavable",
    de: "Filterfeinheit 1 bis 200 µm | Regenerierbare Bauform",
    fr: "Seuils 1 à 200 µm | Conception nettoyable",
    pt: "Micragens de 1 a 200 µm | Design lavável"
  },
  link: "/FilterCartridgesPage",
  linkLabel: {
    en: "Explore filter cartridges",
    zh: "浏览滤芯产品",
    es: "Explorar cartuchos filtrantes",
    de: "Filterkerzen entdecken",
    fr: "Découvrir les cartouches",
    pt: "Explorar cartuchos filtrantes"
  }
}, {
  title: {
    en: "Screen packs",
    zh: "多层组合滤网",
    es: "Paquetes de filtros",
    de: "Filterpakete",
    fr: "Packs de filtres",
    pt: "Pacotes de telas"
  },
  category: {
    en: "Polymer & extrusion",
    zh: "聚合物与挤出过滤",
    es: "Polímeros y extrusión",
    de: "Polymer- & Extrusionsfiltration",
    fr: "Polymères & extrusion",
    pt: "Polímeros e extrusão"
  },
  description: {
    en: "Multi-layer spot welded or aluminum framed screen packs designed for polymer melt filtration and high-pressure resin screening.",
    zh: "多层点焊或铝包边组合滤网片，专为聚合物熔体过滤及高压树脂筛分设计。",
    es: "Paquetes multicapa soldados por puntos o con marco de aluminio diseñados para filtración de polímeros fundidos y resinas a alta presión.",
    de: "Mehrlagige punktgeschweißte oder aluminiumgefasste Siebpakete für Polymerschmelzefiltration und Hochdruckharzsiebung.",
    fr: "Packs multicouches soudés par points ou sertis aluminium conçus pour la filtration de polymères fondus et résines haute pression.",
    pt: "Pacotes multicamadas soldados por pontos ou emoldurados em alumínio para filtração de polímeros fundidos e resinas sob alta pressão."
  },
  specs: {
    en: "Spot-welded or aluminum rimmed | 20 to 500 mesh",
    zh: "点焊或铝包边工艺 | 20 至 500 目配置",
    es: "Soldadura por puntos o marco de aluminio | 20 a 500 mallas",
    de: "Punktgeschweißt oder aluminiumgefasst | 20 bis 500 Mesh",
    fr: "Soudé par points ou cerclé aluminium | 20 à 500 mailles",
    pt: "Soldado por pontos ou com aro de alumínio | 20 a 500 malhas"
  },
  link: "/ScreenPacksPage",
  linkLabel: {
    en: "Explore screen packs",
    zh: "浏览组合滤网",
    es: "Explorar paquetes de filtros",
    de: "Filterpakete entdecken",
    fr: "Découvrir les packs de filtres",
    pt: "Explorar pacotes de telas"
  }
}];
interface SizingRuleItem {
  factor: L6;
  guideline: L6;
}
const sizingRules: SizingRuleItem[] = [{
  factor: {
    en: "Particle retention & micron rating",
    zh: "颗粒截留与微米过滤精度",
    es: "Retención de partículas y grado de micras",
    de: "Partikelrückhaltung & Filterfeinheit",
    fr: "Rétention des particules & seuil de filtration",
    pt: "Retenção de partículas e classificação em mícrons"
  },
  guideline: {
    en: "Select aperture size based on critical cut point rather than nominal mesh count to avoid premature blinding.",
    zh: "根据临界截留粒径（Cut Point）而非仅凭名义目数选择孔径，以避免滤网过早堵塞（盲孔）。",
    es: "Seleccione el tamaño de abertura según el punto de corte crítico en lugar del número nominal de mallas para evitar colmataciones prematuras.",
    de: "Wählen Sie die Maschenweite anhand des kritischen Trennpunkts statt der nominalen Maschenzahl, um frühzeitiges Zusetzen zu vermeiden.",
    fr: "Sélectionnez la taille d'ouverture selon le seuil de coupure critique plutôt que le nombre nominal de mailles pour éviter le colmatage prématuré.",
    pt: "Selecione o tamanho da abertura com base no ponto de corte crítico e não apenas na contagem nominal de malha para evitar entupimento prematuro."
  }
}, {
  factor: {
    en: "Differential pressure tolerance",
    zh: "差压耐受与结构强度 (Delta-P)",
    es: "Tolerancia de presión diferencial (Delta-P)",
    de: "Differenzdruck-Toleranz (Delta-P)",
    fr: "Tolérance à la pression différentielle (Delta-P)",
    pt: "Tolerância à pressão diferencial (Delta-P)"
  },
  guideline: {
    en: "Evaluate max clean Delta-P and calculate structural collapse pressure under continuous operating conditions.",
    zh: "评估初始洁净压差，并在连续运行工况下严格核算结构抗压溃极限。",
    es: "Evalúe la Delta-P limpia máxima y calcule la presión de colapso estructural en condiciones de operación continua.",
    de: "Bewerten Sie das maximale Rein-Delta-P und berechnen Sie den Kollapsdruck bei Dauerbetriebsbedingungen.",
    fr: "Évaluez le Delta-P propre maximal et calculez la pression d'écrasement structurel en conditions de fonctionnement continu.",
    pt: "Avalie o Delta-P limpo máximo e calcule a pressão de colapso estrutural sob condições de operação contínua."
  }
}, {
  factor: {
    en: "Face velocity & flow rate",
    zh: "过滤面流速与通量控制",
    es: "Velocidad frontal y control de caudal",
    de: "Anströmgeschwindigkeit & Durchfluss",
    fr: "Vitesse frontale & débit",
    pt: "Velocidade superficial e vazão"
  },
  guideline: {
    en: "Maintain fluid approach velocity under 0.05 m/s for fine wire meshes to minimize turbulence and wire deflection.",
    zh: "对于精密细丝网，建议将流体迎面流速控制在 0.05 m/s 以下，以减少湍流磨损与丝网形变。",
    es: "Mantenga la velocidad de aproximación del fluido por debajo de 0,05 m/s para mallas finas con el fin de minimizar turbulencias y deflexión del alambre.",
    de: "Halten Sie die Anströmgeschwindigkeit für Feindrahtgewebe unter 0,05 m/s, um Turbulenzabrieb und Drahtverbiegung zu minimieren.",
    fr: "Maintenez la vitesse d'approche du fluide sous 0,05 m/s pour les toiles fines afin de réduire la turbulence et la déflexion des fils.",
    pt: "Mantenha a velocidade de aproximação do fluido abaixo de 0,05 m/s para malhas finas a fim de minimizar turbulência e deflexão do arame."
  }
}, {
  factor: {
    en: "Layer stacking & drainage",
    zh: "层间复合与流道支撑 (Drainage)",
    es: "Superposición de capas y drenaje",
    de: "Lagenaufbau & Entwässerung",
    fr: "Empilement des couches & drainage",
    pt: "Sobreposição de camadas e drenagem"
  },
  guideline: {
    en: "Employ coarse square backing mesh (10 to 40 mesh) beneath fine cloth to provide adequate flow channels.",
    zh: "在精密过滤层下方配置粗方孔支撑网（10 至 40 目），以提供畅通的导流排出通道。",
    es: "Utilice una malla de soporte cuadrada gruesa (10 a 40 mallas) debajo de la tela fina para garantizar canales de flujo adecuados.",
    de: "Setzen Sie grobes Vierkant-Stützgewebe (10 bis 40 Mesh) unter dem Feinfiltertuch ein, um freie Strömungskanäle bereitzustellen.",
    fr: "Utilisez une toile de support carrée grossière (10 à 40 mailles) sous la toile fine pour assurer des canaux d'écoulement dégagés.",
    pt: "Utilize malha de suporte quadrada grossa (10 a 40 malhas) sob o tecido fino para fornecer canais de fluxo adequados."
  }
}];
interface SealingOptionItem {
  type: L6;
  bestFor: L6;
  advantage: L6;
}
const sealingOptions: SealingOptionItem[] = [{
  type: {
    en: "Resistance spot welding",
    zh: "电阻点焊工艺 (Spot Welding)",
    es: "Soldadura por puntos por resistencia",
    de: "Widerstandspunktschweißen",
    fr: "Soudage par points par résistance",
    pt: "Soldagem por pontos por resistência"
  },
  bestFor: {
    en: "Multi-layer screen packs",
    zh: "多层组合滤网片",
    es: "Paquetes de filtros multicapa",
    de: "Mehrlagige Siebpakete",
    fr: "Packs filtrants multicouches",
    pt: "Pacotes de telas multicamadas"
  },
  advantage: {
    en: "Zero added material, maintains full aperture integrity around edges.",
    zh: "无额外添加焊料，确保边缘附近网孔几何形状与通孔率完整。",
    es: "Sin material de aporte, mantiene la integridad geométrica de la abertura en los bordes.",
    de: "Kein Zusatzwerkstoff, erhält die volle Porengeometrie und Durchlässigkeit im Randbereich.",
    fr: "Aucun métal d'apport, préserve l'intégrité géométrique des mailles en périphérie.",
    pt: "Sem adição de material, mantém a integridade geométrica das aberturas nas bordas."
  }
}, {
  type: {
    en: "Aluminum or copper rimming",
    zh: "铝制或紫铜包边 (Rimming)",
    es: "Borde de aluminio o cobre",
    de: "Aluminium- oder Kupfereinfassung",
    fr: "Sertissage aluminium ou cuivre",
    pt: "Borda em alumínio ou cobre"
  },
  bestFor: {
    en: "Extruder breaker plates",
    zh: "挤出机多孔板 / 换网器",
    es: "Placas rompedoras de extrusoras",
    de: "Extruder-Lochplatten / Siebwechsler",
    fr: "Plaques perforées d'extrudeuses",
    pt: "Placas perfuradas de extrusoras"
  },
  advantage: {
    en: "Deformable metal edge prevents polymer bypass and ensures gas-tight fit.",
    zh: "可延展金属包边可完全填补机械公差，杜绝熔体旁路泄漏并确保气密贴合。",
    es: "El borde metálico deformable evita el bypass del polímero y garantiza un ajuste estanco.",
    de: "Verformbarer Metallrand dichtet Toleranzen zuverlässig ab und verhindert Polymerschmelze-Bypass.",
    fr: "La bordure métallique déformable comble les jeux mécaniques et élimine les fuites de polymère.",
    pt: "A borda metálica deformável veda tolerâncias mecânicas, evitando bypass de polímero."
  }
}, {
  type: {
    en: "TIG / plasma seam welding",
    zh: "TIG 氩弧焊 / 等离子直缝焊",
    es: "Soldadura longitudinal TIG / plasma",
    de: "WIG- / Plasma-Längsnahtschweißen",
    fr: "Soudage longitudinal TIG / plasma",
    pt: "Soldagem longitudinal TIG / plasma"
  },
  bestFor: {
    en: "Cylindrical elements & cartridges",
    zh: "圆筒滤网与滤芯",
    es: "Elementos cilíndricos y cartuchos",
    de: "Zylinderkerzen & Filterelemente",
    fr: "Éléments cylindriques & cartouches",
    pt: "Elementos cilíndricos e cartuchos"
  },
  advantage: {
    en: "Hermetic longitudinal and cap seams with full parent metal strength.",
    zh: "纵向缝及端盖气密焊接，接缝强度可靠匹配母材级别。",
    es: "Costuras longitudinales y tapas herméticas con resistencia del metal base.",
    de: "Gasdichte Längs- und Kappenkanten mit Festigkeit des Grundwerkstoffs.",
    fr: "Soudures longitudinales et embouts étanches avec résistance du métal de base.",
    pt: "Costuras longitudinais e tampas herméticas com resistência do metal base."
  }
}, {
  type: {
    en: "PTFE / FKM elastomeric gaskets",
    zh: "PTFE / 氟橡胶 (FKM) 密封圈",
    es: "Juntas elastoméricas de PTFE / FKM",
    de: "PTFE- / FKM-Elastomerdichtungen",
    fr: "Joints élastomères PTFE / FKM",
    pt: "Gaxetas elastoméricas em PTFE / FKM"
  },
  bestFor: {
    en: "Sanitary housing assemblies",
    zh: "卫生级过滤器壳体总成",
    es: "Conjuntos de carcasas sanitarias",
    de: "Sanitär-Gehäusebaugruppen",
    fr: "Corps de filtres sanitaires",
    pt: "Conjuntos de carcaças sanitárias"
  },
  advantage: {
    en: "Replaceable seals resistant to harsh CIP cycles and organic solvents.",
    zh: "可更换密封件，耐受强酸强碱在线清洗 (CIP) 及严苛有机溶剂。",
    es: "Sellos reemplazables resistentes a ciclos CIP exigentes y disolventes orgánicos.",
    de: "Austauschbare Dichtungen beständig gegen scharfe CIP-Reinigung und organische Lösungsmittel.",
    fr: "Joints remplaçables résistant aux cycles NEP sévères et solvants organiques.",
    pt: "Vedações substituíveis resistentes a ciclos CIP rigorosos e solventes orgânicos."
  }
}];
interface CompatibilityItem {
  medium: L6;
  grade304: L6;
  grade316L: L6;
}
const compatibilityMatrix: CompatibilityItem[] = [{
  medium: {
    en: "Deionized & potable water",
    zh: "去离子水与饮用水",
    es: "Agua desionizada y potable",
    de: "Deionisiertes & Trinkwasser",
    fr: "Eau désionisée et potable",
    pt: "Água deionizada e potável"
  },
  grade304: {
    en: "Recommended",
    zh: "推荐使用",
    es: "Recomendado",
    de: "Empfohlen",
    fr: "Recommandé",
    pt: "Recomendado"
  },
  grade316L: {
    en: "Recommended",
    zh: "推荐使用",
    es: "Recomendado",
    de: "Empfohlen",
    fr: "Recommandé",
    pt: "Recomendado"
  }
}, {
  medium: {
    en: "Marine & chloride solutions",
    zh: "海洋环境与含氯化物溶液",
    es: "Ambientes marinos y soluciones con cloruros",
    de: "Meeresatmosphäre & Chloridlösungen",
    fr: "Milieux marins & solutions chlorées",
    pt: "Ambientes marítimos e soluções cloradas"
  },
  grade304: {
    en: "Pitting risk",
    zh: "存在点蚀风险",
    es: "Riesgo de picaduras",
    de: "Lochfraßrisiko",
    fr: "Risque de piqûres",
    pt: "Risco de corrosão por pite"
  },
  grade316L: {
    en: "Suitable (<200 ppm Cl⁻)",
    zh: "适用 (<200 ppm Cl⁻)",
    es: "Adecuado (<200 ppm Cl⁻)",
    de: "Geeignet (<200 ppm Cl⁻)",
    fr: "Adapté (<200 ppm Cl⁻)",
    pt: "Adequado (<200 ppm Cl⁻)"
  }
}, {
  medium: {
    en: "Hot organic acids & solvents",
    zh: "高温有机酸与有机溶剂",
    es: "Ácidos orgánicos calientes y disolventes",
    de: "Heiße organische Säuren & Lösemittel",
    fr: "Acides organiques chauds et solvants",
    pt: "Ácidos orgânicos quentes e solventes"
  },
  grade304: {
    en: "Moderate",
    zh: "耐受度中等",
    es: "Moderada",
    de: "Mäßig",
    fr: "Modérée",
    pt: "Moderada"
  },
  grade316L: {
    en: "Recommended",
    zh: "推荐使用",
    es: "Recomendado",
    de: "Empfohlen",
    fr: "Recommandé",
    pt: "Recomendado"
  }
}, {
  medium: {
    en: "High-viscosity polymer melts",
    zh: "高粘度聚合物熔体",
    es: "Polímeros fundidos de alta viscosidad",
    de: "Hochviskose Polymerschmelzen",
    fr: "Polymères fondus à haute viscosité",
    pt: "Polímeros fundidos de alta viscosidade"
  },
  grade304: {
    en: "Suitable (dry)",
    zh: "适用 (干燥无腐蚀)",
    es: "Adecuado (seco)",
    de: "Geeignet (trocken)",
    fr: "Adapté (sec)",
    pt: "Adequado (seco)"
  },
  grade316L: {
    en: "Recommended",
    zh: "推荐使用",
    es: "Recomendado",
    de: "Empfohlen",
    fr: "Recommandé",
    pt: "Recomendado"
  }
}];
interface FaqItem {
  q: L6;
  a: L6;
}
const faqs: FaqItem[] = [{
  q: {
    en: "How do I choose between plain weave, twilled Dutch, and sintered wire cloth?",
    zh: "如何在平纹编织网、斜纹荷兰编织网和多层烧结网之间进行选型？",
    es: "¿Cómo elijo entre tejido liso, tejido Dutch cruzado y tela metálica sinterizada?",
    de: "Wie wähle ich zwischen Leinwandbindung, Köpertresse und Sintermetallgewebe?",
    fr: "Comment choisir entre tissage uni, tissage hollandais sergé et toile métallique frittée ?",
    pt: "Como escolher entre trama lisa, trama holandesa sarja e tela metálica sinterizada?"
  },
  a: {
    en: "Plain square mesh is standard for high-flow sieving above 50 microns. Twilled Dutch weaves handle surface and depth filtration down to 5 microns under pressure. Sintered laminates are required when filter media needs structural rigidity without external support perforated cores.",
    zh: "平纹方孔网常用于 50 微米以上的大流量筛分与表层过滤；斜纹荷兰编织网可承受高压并在 5 微米至 100 微米区间实现高精度表层与深层截留；多层烧结网在无需外部冲孔支撑管的情况下即可提供极高的结构刚性与耐压抗冲刷性能。",
    es: "La malla cuadrada lisa es estándar para tamizado de alto caudal por encima de 50 micras. Los tejidos Dutch cruzados manejan filtración superficial y profunda hasta 5 micras bajo presión. Los laminados sinterizados son necesarios cuando el medio filtrante requiere rigidez estructural sin núcleos perforados externos.",
    de: "Vierkant-Leinwandgewebe ist Standard für Durchflusssiebung über 50 µm. Köpertressen eignen sich für Oberflächen- und Tiefenfiltration bis 5 µm unter Druck. Sinterlaminate sind erforderlich, wenn Filtermedien hohe Formstabilität ohne externe Lochblechstützrohre benötigen.",
    fr: "La toile carrée unie convient au tamisage à grand débit au-dessus de 50 microns. Les tissages hollandais sergés assurent une filtration de surface et de profondeur jusqu'à 5 microns sous pression. Les stratifiés frittés s'imposent lorsque le média exige une rigidité sans noyau perforé externe.",
    pt: "A malha quadrada lisa é padrão para peneiramento de alta vazão acima de 50 mícrons. As tramas holandesas sarjas atendem à filtração de superfície e profundidade até 5 mícrons sob pressão. Os laminados sinterizados são indicados quando o meio filtrante exige rigidez estrutural sem tubos perfurados externos."
  }
}, {
  q: {
    en: "What tolerance standards do you adhere to for stamped mesh parts?",
    zh: "冲压滤网加工件执行哪些公差与质量标准？",
    es: "¿Qué normas de tolerancia aplican a las piezas estampadas de malla metálica?",
    de: "Welche Toleranznormen gelten für gestanzte Drahtgewebeteile?",
    fr: "Quelles normes de tolérance appliquez-vous aux pièces embouties en toile métallique ?",
    pt: "Quais normas de tolerância são seguidas para peças estampadas de tela metálica?"
  },
  a: {
    en: "We manufacture according to ISO 9044 and ASTM E2016 tolerances, holding outer diameters to +/-0.1 mm on precision laser cuts and +/-0.25 mm on standard mechanical die-stamped discs.",
    zh: "我们严格遵循 ISO 9044 和 ASTM E2016 工业制造标准。精密激光切割外径公差可达 ±0.1 mm，常规机械模具冲压滤圆片公差控制在 ±0.25 mm 以内。",
    es: "Fabricamos según las tolerancias de ISO 9044 y ASTM E2016, manteniendo diámetros exteriores de ±0,1 mm en corte por láser de precisión y ±0,25 mm en discos estampados mecánicos estándar.",
    de: "Wir fertigen nach den Toleranzen der ISO 9044 und ASTM E2016 mit Außendurchmessertoleranzen von ±0,1 mm beim Präzisions-Laserschneiden und ±0,25 mm bei gestanzten Ronden.",
    fr: "Nous fabriquons selon les tolérances ISO 9044 et ASTM E2016, avec des tolérances de diamètre extérieur de ±0,1 mm en découpe laser de précision et ±0,25 mm pour les disques emboutis standard.",
    pt: "Fabricamos conforme as tolerâncias ISO 9044 e ASTM E2016, mantendo diâmetros externos de ±0,1 mm em corte a laser de precisão e ±0,25 mm em discos estampados convencionais."
  }
}, {
  q: {
    en: "Can custom wire diameters be woven for specific burst pressure ratings?",
    zh: "是否可以针对特定爆破压差定制特殊丝径与织造规格？",
    es: "¿Se pueden tejer diámetros de alambre a medida para presiones de rotura específicas?",
    de: "Können Sonderdrahtdurchmesser für definierte Berstdrücke gewebt werden?",
    fr: "Peut-on tisser des diamètres de fil sur mesure pour des pressions d'éclatement spécifiques ?",
    pt: "É possível tecer diâmetros de arame sob medida para pressões de ruptura específicas?"
  },
  a: {
    en: "Yes. Our engineering team calculates wire tensile requirements and open area tradeoffs, configuring custom warp and weft counts tailored to your system operating envelope.",
    zh: "可以。我们的工程技术团队将综合计算金属丝抗拉强度与开孔率平衡，针对您的系统工况和工作极限定制经纬丝径与目数组合。",
    es: "Sí. Nuestro equipo de ingeniería calcula la resistencia a la tracción del alambre y el equilibrio del área abierta, configurando urdimbres y tramas personalizadas según sus condiciones de operación.",
    de: "Ja. Unser Ingenieurteam berechnet Drahtzugfestigkeiten und Kompromisse bei der offenen Siebfläche und konfiguriert Kett- und Schussdrahtzahlen passend zu Ihrem Betriebsfenster.",
    fr: "Oui. Notre équipe technique calcule la résistance à la traction des fils et le compromis de surface ouverte, configurant le nombre de fils en chaîne et trame selon vos conditions de service.",
    pt: "Sim. Nossa equipe de engenharia calcula a resistência à tração dos arames e o equilíbrio de área aberta, configurando urdume e trama adaptados às condições de operação do sistema."
  }
}];
const copy = {
  eyebrow: {
    en: "Technical Handbook",
    zh: "技术选型手册",
    es: "Manual técnico",
    de: "Technisches Handbuch",
    fr: "Manuel technique",
    pt: "Manual técnico"
  },
  h2: {
    en: "Filter element engineering and selection guide",
    zh: "过滤元件工程设计与选型指南",
    es: "Guía de ingeniería y selección de elementos filtrantes",
    de: "Leitfaden zur Auslegung und Auswahl von Filterelementen",
    fr: "Guide d'ingénierie et de sélection des éléments filtrants",
    pt: "Guia de engenharia e seleção de elementos filtrantes"
  },
  lead: {
    en: "Evaluate geometry, flow sizing, boundary sealing, and alloy suitability for reliable filtration across process environments.",
    zh: "全面评估几何结构、流量尺寸、边缘密封工艺与合金兼容性，确保工业流程中的长效稳定过滤。",
    es: "Evalúe geometría, dimensionamiento de flujo, sellado perimetral y compatibilidad de aleaciones para una filtración fiable en entornos de proceso.",
    de: "Bewerten Sie Geometrie, Durchflussauslegung, Randabdichtung und Werkstoffeignung für zuverlässige Filtration in Prozessumgebungen.",
    fr: "Évaluez la géométrie, le dimensionnement du débit, l'étanchéité périphérique et la compatibilité des alliages pour une filtration fiable.",
    pt: "Avalie geometria, dimensionamento de fluxo, vedação perimetral e compatibilidade de ligas para filtração confiável nos processos industriais."
  },
  tab1: {
    en: "1. Component types",
    zh: "1. 组件类型",
    es: "1. Tipos de componentes",
    de: "1. Komponententypen",
    fr: "1. Types de composants",
    pt: "1. Tipos de componentes"
  },
  tab2: {
    en: "2. Flow & sizing",
    zh: "2. 流量与尺寸",
    es: "2. Flujo y dimensiones",
    de: "2. Durchfluss & Auslegung",
    fr: "2. Débit & dimensionnement",
    pt: "2. Fluxo e dimensionamento"
  },
  tab3: {
    en: "3. Edge sealing",
    zh: "3. 边缘密封",
    es: "3. Sellado de bordes",
    de: "3. Randabdichtung",
    fr: "3. Étanchéité des bords",
    pt: "3. Vedação de bordas"
  },
  tab4: {
    en: "4. Alloy compatibility",
    zh: "4. 合金兼容性",
    es: "4. Compatibilidad de aleaciones",
    de: "4. Werkstoffkompatibilität",
    fr: "4. Compatibilité des alliages",
    pt: "4. Compatibilidade de ligas"
  },
  sizingHeading: {
    en: "Geometric and fluid dynamic sizing",
    zh: "几何结构与流体动力学计算",
    es: "Dimensionamiento geométrico y dinámico de fluidos",
    de: "Geometrische und strömungstechnische Auslegung",
    fr: "Dimensionnement géométrique et dynamique des fluides",
    pt: "Dimensionamento geométrico e de dinâmica de fluidos"
  },
  sizingDesc: {
    en: "Key parameters required to size stainless mesh elements without flow restrictions or early blinding.",
    zh: "选型不锈钢滤网元件所需的核心参数，确保无流量瓶颈与过早堵塞。",
    es: "Parámetros clave requeridos para dimensionar elementos de malla inoxidable sin restricciones de flujo ni colmatación prematura.",
    de: "Wesentliche Kenngrößen zur Auslegung von Edelstahlgewebe-Elementen ohne Durchflussengpässe oder vorzeitiges Zusetzen.",
    fr: "Paramètres essentiels pour dimensionner les éléments en toile inox sans restriction de débit ni colmatage précoce.",
    pt: "Parâmetros essenciais para dimensionar elementos de malha de aço inoxidável sem restrições de fluxo ou entupimento precoce."
  },
  calcRefTitle: {
    en: "Quick Sizing Reference",
    zh: "快速计算参考",
    es: "Referencia rápida de cálculo",
    de: "Schnell-Auslegungsreferenz",
    fr: "Référence de calcul rapide",
    pt: "Referência rápida de cálculo"
  },
  openAreaFormulaTitle: {
    en: "Open Area Formula:",
    zh: "开孔率 (Open Area) 计算公式：",
    es: "Fórmula de superficie abierta:",
    de: "Formel für offene Siebfläche:",
    fr: "Formule de surface ouverte :",
    pt: "Fórmula de área aberta:"
  },
  openAreaVariables: {
    en: "w = aperture width, d = wire diameter",
    zh: "w = 网孔宽度 (Aperture), d = 丝径 (Wire Diameter)",
    es: "w = ancho de abertura, d = diámetro del alambre",
    de: "w = Maschenweite, d = Drahtdurchmesser",
    fr: "w = largeur d'ouverture, d = diamètre du fil",
    pt: "w = abertura da malha, d = diâmetro do arame"
  },
  deltaPCallout: {
    en: "Need exact pressure drop calculations across specific mesh meshes and flow media?",
    zh: "需要针对特定目数和介质进行精确的压降 (Delta-P) 计算？",
    es: "¿Necesita cálculos exactos de caída de presión para mallas específicas y fluidos de proceso?",
    de: "Benötigen Sie exakte Druckverlustberechnungen für bestimmte Maschenweiten und Medien?",
    fr: "Besoin de calculs précis de perte de charge pour des toiles spécifiques et fluides de procédé ?",
    pt: "Precisa de cálculos exatos de queda de pressão para malhas e fluidos específicos?"
  },
  viewCalcChartsBtn: {
    en: "View calculation charts",
    zh: "查看工程计算图表",
    es: "Ver tablas de cálculo",
    de: "Berechnungsdiagramme ansehen",
    fr: "Consulter les abaques de calcul",
    pt: "Ver gráficos de cálculo"
  },
  thMedium: {
    en: "Operating medium",
    zh: "工作介质",
    es: "Medio de operación",
    de: "Betriebsmedium",
    fr: "Milieu de service",
    pt: "Meio operacional"
  },
  thTemp: {
    en: "Temperature",
    zh: "适用温度",
    es: "Temperatura",
    de: "Temperatur",
    fr: "Température",
    pt: "Temperatura"
  },
  compatibilityNote: {
    en: "For non-standard operating conditions, contact our technical team with your media chemistry, temperature, and pressure envelope.",
    zh: "针对非标特殊工况，请向我们的工程技术团队提供介质化学成分、温度及压力范围以获取适配建议。",
    es: "Para condiciones de operación no estándar, contacte a nuestro equipo técnico con la composición química del fluido, temperatura y presión.",
    de: "Für abweichende Betriebsbedingungen wenden Sie sich bitte mit Ihren Mediendaten, Betriebstemperaturen und Drücken an unser Technikteam.",
    fr: "Pour des conditions de fonctionnement non standard, consultez notre équipe technique avec la composition de votre fluide, température et pression.",
    pt: "Para condições operacionais não padronizadas, entre em contato com nossa equipe técnica informando a composição do fluido, temperatura e pressão."
  },
  faqHeading: {
    en: "Frequently asked engineering questions",
    zh: "工程与技术常见问题",
    es: "Preguntas frecuentes de ingeniería",
    de: "Häufige Fragen zur Auslegung",
    fr: "Questions fréquentes d'ingénierie",
    pt: "Perguntas frequentes de engenharia"
  },
  faqDesc: {
    en: "Technical specifics regarding tolerances, weaving geometries, and structural ratings.",
    zh: "关于公差标准、织造几何结构和耐压等级的技术细节。",
    es: "Detalles técnicos sobre tolerancias, geometrías de tejido y clasificaciones estructurales.",
    de: "Technische Details zu Toleranzen, Webgeometrien und Belastbarkeitsklassen.",
    fr: "Détails techniques concernant les tolérances, géométries de tissage et tenues en pression.",
    pt: "Detalhes técnicos sobre tolerâncias, geometrias de tecelagem e classes de resistência mecânica."
  },
  inlineCtaText: {
    en: "Require custom mesh specifications or CAD reviews for your component drawings?",
    zh: "需要针对滤芯组件图纸进行定制丝网规格确认或 CAD 工程评审？",
    es: "¿Requiere especificaciones de malla personalizadas o revisión CAD de sus planos de componentes?",
    de: "Benötigen Sie Sondermaße oder eine CAD-Zeichnungsprüfung für Ihre Komponenten?",
    fr: "Besoin de spécifications sur mesure ou d'une revue CAD pour vos plans de pièces ?",
    pt: "Precisa de especificações sob medida ou revisão CAD para seus desenhos de componentes?"
  }
};
export default function SelectionGuide() {
  const {
    lang
  } = useLanguage();
  return <section id="engineeringselectionguide" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-muted text-foreground mf-caption font-mono uppercase tracking-wider mb-4 border border-border">
            <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
            <span>{pick(lang, copy.eyebrow)}</span>
          </div>
          <h2 className="mf-h2 text-foreground font-default mb-4">
            {pick(lang, copy.h2)}
          </h2>
          <p className="mf-lead text-muted-foreground font-default">
            {pick(lang, copy.lead)}
          </p>
        </div>

        {/* 4-Pillar Tabs Section */}
        <Tabs defaultValue="components" className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-4 mb-8">
            <TabsList className="bg-muted p-1 rounded-lg flex-wrap h-auto">
              <TabsTrigger value="components" className="px-4 py-2 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Layers className="size-4 mr-2" aria-hidden="true" />
                {pick(lang, copy.tab1)}
              </TabsTrigger>
              <TabsTrigger value="sizing" className="px-4 py-2 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Maximize2 className="size-4 mr-2" aria-hidden="true" />
                {pick(lang, copy.tab2)}
              </TabsTrigger>
              <TabsTrigger value="sealing" className="px-4 py-2 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <ShieldCheck className="size-4 mr-2" aria-hidden="true" />
                {pick(lang, copy.tab3)}
              </TabsTrigger>
              <TabsTrigger value="compatibility" className="px-4 py-2 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Cpu className="size-4 mr-2" aria-hidden="true" />
                {pick(lang, copy.tab4)}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: COMPONENT TYPES */}
          <TabsContent value="components" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {componentTypes.map((comp, i) => <div key={i} data-index={i}>
                  <Card className="h-full flex flex-col justify-between border-border bg-card transition-[border-color]">
                    <CardHeader>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <Badge variant="outline" className="mf-caption font-mono font-normal">
                          {pick(lang, comp.category)}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        {pick(lang, comp.title)}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground mt-2">
                        {pick(lang, comp.description)}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="p-3 bg-muted rounded-md mf-caption font-mono text-muted-foreground mb-6 border border-border">
                        {pick(lang, comp.specs)}
                      </div>
                      <Button asChild variant="outline" className="w-full justify-between">
                        <Link to={comp.link}>
                          <span>{pick(lang, comp.linkLabel)}</span>
                          <ArrowRight className="size-4 ml-1" aria-hidden="true" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
          </TabsContent>

          {/* TAB 2: SIZING & FLOW */}
          <TabsContent value="sizing" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7 space-y-4">
                <div className="mb-4">
                  <h3 className="mf-h3 font-bold text-foreground mb-1">
                    {pick(lang, copy.sizingHeading)}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {pick(lang, copy.sizingDesc)}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {sizingRules.map((rule, i) => <div key={i} data-index={i} className="p-4 rounded-lg bg-card border border-border flex items-start gap-3">
                      <CircleCheck className="size-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                      <div>
                        <h4 className="font-semibold text-sm text-card-foreground">
                          {pick(lang, rule.factor)}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {pick(lang, rule.guideline)}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="lg:col-span-5 bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4 text-primary">
                  <FileSpreadsheet className="size-5" aria-hidden="true" />
                  <span className="font-semibold text-sm">
                    {pick(lang, copy.calcRefTitle)}
                  </span>
                </div>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div className="p-3 bg-muted rounded-md border border-border font-mono mf-caption">
                    <p className="font-bold text-foreground">
                      {pick(lang, copy.openAreaFormulaTitle)}
                    </p>
                    <p className="mt-1 text-foreground">OA (%) = (w / (w + d))² × 100</p>
                    <p className="text-muted-foreground mt-1">
                      {pick(lang, copy.openAreaVariables)}
                    </p>
                  </div>
                  <p>
                    {pick(lang, copy.deltaPCallout)}
                  </p>
                  <Button asChild className="w-full">
                    <Link to="/TechnicalResourcesPage">
                      {pick(lang, copy.viewCalcChartsBtn)}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* TAB 3: SEALING */}
          <TabsContent value="sealing" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sealingOptions.map((seal, i) => <div key={i} data-index={i} className="bg-card border border-border rounded-lg p-6 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h4 className="font-bold text-base text-card-foreground">
                        {pick(lang, seal.type)}
                      </h4>
                      <Badge variant="outline" className="mf-caption font-mono bg-muted">
                        {pick(lang, seal.bestFor)}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      {pick(lang, seal.advantage)}
                    </p>
                  </div>
                </div>)}
            </div>
          </TabsContent>

          {/* TAB 4: COMPATIBILITY */}
          <TabsContent value="compatibility" className="focus-visible:outline-none">
            <div className="overflow-x-auto border border-border rounded-lg bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/70">
                    <TableHead className="font-semibold text-foreground">
                      {pick(lang, copy.thMedium)}
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">AISI 304</TableHead>
                    <TableHead className="font-semibold text-foreground">AISI 316L</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {compatibilityMatrix.map((row, i) => <TableRow key={i} data-index={i}>
                      <TableCell className="font-medium text-foreground">
                        {pick(lang, row.medium)}
                      </TableCell>
                      <TableCell className="text-foreground">
                        {pick(lang, row.grade304)}
                      </TableCell>
                      <TableCell className="text-foreground font-medium">
                        {pick(lang, row.grade316L)}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center gap-2 mt-4 mf-caption text-muted-foreground px-1">
              <CircleAlert className="size-4 shrink-0 text-primary" aria-hidden="true" />
              <span>
                {pick(lang, copy.compatibilityNote)}
              </span>
            </div>
          </TabsContent>
        </Tabs>

        {/* Engineering FAQ / Decision Accordion */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="max-w-3xl mb-8">
            <h3 className="mf-h3 font-bold text-foreground font-default">
              {pick(lang, copy.faqHeading)}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {pick(lang, copy.faqDesc)}
            </p>
          </div>

          <div className="max-w-4xl bg-card border border-border rounded-lg p-6">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border">
                  <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline hover:text-primary">
                    {pick(lang, faq.q)}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {pick(lang, faq.a)}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        {/* Single mid-page prompt using InlineCta */}
        <div className="mt-12">
          <InlineCta text={pick(lang, copy.inlineCtaText)} buttonLabel={pick(lang, COMMON.requestQuote)} to="/RequestAQuotePage" />
        </div>
      </div>
    </section>;
}